import { z } from "zod";
import { useState } from "react";
import { cn } from "@/shared/lib/utils";
import { Skeleton } from "@/shared/components";
import { ExternalLink, ImageOff } from "lucide-react";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const ImageCardSchema = z.object({
  imageUrl: z.string(),
  title: z.string(),
  description: z.string().optional(),
  meta: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      }),
    )
    .optional(),
  sourceUrl: z.string().optional(),
});

type ImageCardData = z.infer<typeof ImageCardSchema>;

/* ────────────────────── Component ────────────────────── */

export function ImageCard({
  widget,
  className,
}: AgentWidgetProps<ImageCardData>) {
  const { imageUrl, title, description, meta, sourceUrl } = widget.data;
  const [imgError, setImgError] = useState(false);

  const handleSourceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (sourceUrl) window.open(sourceUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-stroke-subtle shadow-sm",
        className,
      )}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video w-full overflow-hidden bg-surface-sunken">
        {imgError ? (
          <div className="flex h-full w-full items-center justify-center">
            <ImageOff className="size-8 text-text-tertiary/40" />
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={title}
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
            loading="lazy"
          />
        )}
        {/* Source link badge */}
        {sourceUrl && (
          <button
            onClick={handleSourceClick}
            className={cn(
              "absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-black/60 px-2 py-1",
              "text-[10px] font-medium text-white backdrop-blur-sm",
              "transition-opacity hover:bg-black/80",
            )}
          >
            <ExternalLink className="size-2.5" />
            원문 보기
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        <p className="line-clamp-2 text-[13px] font-semibold leading-snug text-text-primary">
          {title}
        </p>

        {description && (
          <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-text-tertiary">
            {description}
          </p>
        )}

        {meta && meta.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
            {meta.map((item, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <span className="text-[10px] text-text-tertiary">
                  {item.label}
                </span>
                <span className="text-[10px] font-medium text-text-primary">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function ImageCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-stroke-subtle bg-surface-raised",
        className,
      )}
    >
      {/* Thumbnail skeleton */}
      <Skeleton className="aspect-video w-full rounded-none" />
      {/* Content skeleton */}
      <div className="space-y-2 p-3">
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-3 w-3/5" />
        <div className="flex gap-3 pt-1">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
    </div>
  );
}

/* ────────────────────── Widget Definition ────────────────────── */

const imageCardWidget = defineWidget<ImageCardData>({
  type: WIDGET_TYPES.IMAGE_CARD,
  label: "콘텐츠 이미지 카드",
  schema: ImageCardSchema,
  component: ImageCard,
  skeleton: ImageCardSkeleton,
  defaultLayout: "half",
  maxHeight: 280,
  aiHint: "콘텐츠 썸네일 + 제목 + 설명 + 메타 카드 (블로그, 뉴스, 상품 등)",
});

registerWidget(imageCardWidget);
